import { supabase } from '@/lib/supabase-client'
import { RealtimePostgresChangesPayload } from '@supabase/supabase-js'
import { ref } from 'vue'

interface SubscriptionChannels {
  vehicles: any;
  customerData: any;
  customerNotes: any;
  creditInfo: any;
  loanInfo: any;
  notifications: any;
}

class RealtimeService {
  private channels: Partial<SubscriptionChannels> = {}
  private rooftopId: string | null = null
  public isConnected = ref(false)

  async initialize(rooftopId: string) {
    // Clean up existing subscriptions before reinitializing
    if (this.isConnected.value) {
      this.cleanup()
    }
    
    this.rooftopId = rooftopId
    
    try {
      this.setupVehicleSubscription()
      this.setupCustomerDataSubscription()
      this.setupNotesSubscription()
      this.setupCreditInfoSubscription()
      this.setupLoanInfoSubscription()
      this.setupNotificationsSubscription()
      this.isConnected.value = true
    } catch (error) {
      console.error('Error initializing realtime service:', error)
      this.cleanup()
    }
  }

  private setupVehicleSubscription() {
    console.log('Setting up vehicle subscription')
    try {
      this.channels.vehicles = supabase
        .channel(`vehicles-changes-${this.rooftopId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'vehicles',
            filter: `rooftop_id=eq.${this.rooftopId}`
          },
          (payload) => {
            console.log('Vehicle update received:', payload)
            try {
              window.dispatchEvent(new CustomEvent('vehicle-updated', { 
                detail: payload 
              }))
            } catch (error) {
              console.error('Error handling vehicle update:', error)
            }
          }
        )
        .subscribe()
    } catch (error) {
      console.error('Error setting up vehicle subscription:', error)
    }
  }

  private setupCustomerDataSubscription() {
    console.log('Setting up customer data subscription')
    try {
      this.channels.customerData = supabase
        .channel(`customer-changes-${this.rooftopId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'customer_data',
            filter: `rooftop_id=eq.${this.rooftopId}`
          },
          (payload) => {
            console.log('Customer data update received:', payload)
            try {
              window.dispatchEvent(new CustomEvent('customer-updated', { 
                detail: payload 
              }))
            } catch (error) {
              console.error('Error handling customer update:', error)
            }
          }
        )
        .subscribe()
    } catch (error) {
      console.error('Error setting up customer data subscription:', error)
    }
  }

  private setupNotesSubscription() {
    console.log('Setting up notes subscription')
    try {
      this.channels.customerNotes = supabase
        .channel(`notes-changes-${this.rooftopId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'customer_notes',
            filter: `rooftop_id=eq.${this.rooftopId}`
          },
          (payload) => {
            console.log('Notes update received:', payload)
            try {
              window.dispatchEvent(new CustomEvent('notes-updated', { 
                detail: payload 
              }))
            } catch (error) {
              console.error('Error handling notes update:', error)
            }
          }
        )
        .subscribe()
    } catch (error) {
      console.error('Error setting up notes subscription:', error)
    }
  }

  private setupCreditInfoSubscription() {
    console.log('Setting up credit info subscription')
    try {
      this.channels.creditInfo = supabase
        .channel(`credit-changes-${this.rooftopId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'credit_info',
            filter: `rooftop_id=eq.${this.rooftopId}`
          },
          (payload) => {
            console.log('Credit info update received:', payload)
            try {
              window.dispatchEvent(new CustomEvent('credit-updated', { 
                detail: payload 
              }))
            } catch (error) {
              console.error('Error handling credit update:', error)
            }
          }
        )
        .subscribe()
    } catch (error) {
      console.error('Error setting up credit info subscription:', error)
    }
  }

  private setupLoanInfoSubscription() {
    console.log('Setting up loan info subscription')
    try {
      this.channels.loanInfo = supabase
        .channel(`loan-changes-${this.rooftopId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'loan_info',
            filter: `rooftop_id=eq.${this.rooftopId}`
          },
          (payload) => {
            console.log('Loan info update received:', payload)
            try {
              window.dispatchEvent(new CustomEvent('loan-updated', { 
                detail: payload 
              }))
            } catch (error) {
              console.error('Error handling loan update:', error)
            }
          }
        )
        .subscribe()
    } catch (error) {
      console.error('Error setting up loan info subscription:', error)
    }
  }

  private setupNotificationsSubscription() {
    console.log('Setting up notifications subscription')
    try {
      this.channels.notifications = supabase
        .channel(`notification-changes-${this.rooftopId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'notifications',
            filter: `rooftop_id=eq.${this.rooftopId}`
          },
          (payload) => {
            console.log('Notification update received:', payload)
            try {
              window.dispatchEvent(new CustomEvent('notification-updated', { 
                detail: payload 
              }))
            } catch (error) {
              console.error('Error handling notification update:', error)
            }
          }
        )
        .subscribe()
    } catch (error) {
      console.error('Error setting up notifications subscription:', error)
    }
  }

  cleanup() {
    console.log('Cleaning up realtime subscriptions')
    try {
      Object.values(this.channels).forEach(channel => {
        if (channel) {
          try {
            channel.unsubscribe()
          } catch (error) {
            console.error('Error unsubscribing from channel:', error)
          }
        }
      })
      this.channels = {}
      this.isConnected.value = false
    } catch (error) {
      console.error('Error cleaning up realtime service:', error)
    }
  }
}

export const realtimeService = new RealtimeService() 